import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { StatusSlider } from '../components/StatusSlider';
import { ScheduleModal } from '../components/ScheduleModal';
import { SMSOptionsModal } from '../components/SMSOptionsModal';
import JobExpenses from '../components/JobExpenses';
import { toast } from 'sonner';
import { 
  ArrowLeft, Phone, MapPin, Mail, MessageSquare, 
  DollarSign, Camera, Trash2, X, Image, Calendar, Clock,
  FileDown, Link as LinkIcon, Loader2, User, Key, Repeat
} from 'lucide-react';
import { format, parseISO } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showSMSModal, setShowSMSModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [billableExpenses, setBillableExpenses] = useState(0);
  const [planLimits, setPlanLimits] = useState(null);
  const [childJobs, setChildJobs] = useState([]);
  const [loadingChildren, setLoadingChildren] = useState(false);

  useEffect(() => {
    fetchJob();
    fetchActiveTemplate();
    fetchPlanLimits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchPlanLimits = async () => {
    try {
      const response = await axios.get(`${API}/jobs/limits`);
      setPlanLimits(response.data.plan_limits);
    } catch (error) {
      console.error('Failed to fetch plan limits');
    }
  };

  const fetchActiveTemplate = async () => {
    try {
      const response = await axios.get(`${API}/note-templates/active/current`);
      setActiveTemplate(response.data.template);
    } catch (error) {
      console.error('Failed to fetch active template');
    }
  };

  const handleStartEditing = () => {
    // If customer_notes are empty and we have an active template, pre-fill them
    if ((!job.customer_notes || job.customer_notes.trim() === '') && activeTemplate) {
      setEditForm(prev => ({ ...prev, customer_notes: activeTemplate.template_content }));
    }
    setEditing(true);
  };

  const fetchJob = async () => {
    try {
      const response = await axios.get(`${API}/jobs/${id}`);
      setJob(response.data);
      setEditForm(response.data);
      
      // If this is a recurring parent job, fetch child jobs
      if (response.data.is_recurring) {
        fetchChildJobs(response.data.id);
      }
    } catch (error) {
      console.error('Failed to fetch job:', error);
      toast.error('Job not found');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchChildJobs = async (parentId) => {
    setLoadingChildren(true);
    try {
      const response = await axios.get(`${API}/jobs/${parentId}/children`);
      setChildJobs(response.data.children || []);
    } catch (error) {
      console.error('Failed to fetch child jobs:', error);
    } finally {
      setLoadingChildren(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await axios.post(`${API}/jobs/${id}/status`, { status: newStatus });
      setJob(prev => ({ ...prev, status: newStatus }));
      toast.success(`Status updated to ${newStatus}`);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updates = {
        client_name: editForm.client_name,
        client_phone: editForm.client_phone,
        client_email: editForm.client_email,
        address: editForm.address,
        description: editForm.description,
        quote_amount: parseFloat(editForm.quote_amount),
        notes: editForm.notes,
        customer_notes: editForm.customer_notes
      };
      
      const response = await axios.put(`${API}/jobs/${id}`, updates);
      setJob(response.data);
      setEditing(false);
      toast.success('Job updated');
    } catch (error) {
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this job?')) return;
    
    try {
      await axios.delete(`${API}/jobs/${id}`);
      toast.success('Job deleted');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to delete job');
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingPhoto(true);
    
    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64 = event.target.result;
        
        await axios.post(`${API}/jobs/${id}/photos`, {
          job_id: id,
          photo_data: base64,
          description: 'Job photo'
        });
        
        await fetchJob();
        toast.success('Photo added');
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    try {
      await axios.delete(`${API}/jobs/${id}/photos/${photoId}`);
      await fetchJob();
      toast.success('Photo deleted');
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  const handleScheduled = (updatedJob) => {
    setJob(updatedJob);
  };

  const handleRequestPayment = async () => {
    try {
      const response = await axios.get(`${API}/jobs/${id}/payment-link`);
      const { payment_method, web_link, amount } = response.data;
      
      if (!payment_method) {
        toast.error('Please set up your payment method in Settings');
        return;
      }
      
      const customerPortalUrl = `${window.location.origin}/customer/${job.customer_token}`;
      
      if (job.client_phone) {
        const message = `Hi ${job.client_name}! Your invoice for "${job.description}" is ready.\n\nTotal: $${amount}\n\nView & pay: ${customerPortalUrl}`;
        window.location.href = `sms:${job.client_phone}?body=${encodeURIComponent(message)}`;
      } else {
        await navigator.clipboard.writeText(customerPortalUrl);
        toast.success('Customer portal link copied to clipboard!');
      }
    } catch (error) {
      toast.error('Failed to generate payment link');
    }
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const response = await axios.get(`${API}/jobs/${id}/pdf`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `quote-${job.id.slice(0, 8).toUpperCase()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded!');
    } catch (error) {
      toast.error('Failed to download PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleCopyPortalLink = async () => {
    const portalUrl = `${window.location.origin}/customer/${job.customer_token}`;
    try {
      await navigator.clipboard.writeText(portalUrl);
      toast.success('Customer portal link copied!');
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      return format(parseISO(dateStr), 'EEE, MMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return null;
    try {
      const [hour, min] = timeStr.split(':').map(Number);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${min.toString().padStart(2, '0')} ${ampm}`;
    } catch {
      return timeStr;
    }
  };

  // Handle back navigation - return to where user came from
  const handleBack = () => {
    // Check if we came from a client page
    const fromClient = location.state?.fromClient;
    const clientId = location.state?.clientId || job?.client_id;
    
    if (fromClient && clientId) {
      navigate(`/clients/${clientId}`);
    } else if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/dashboard');
    }
  };

  if (loading) {
    return (
      <div className="p-4">
        <div className="animate-pulse space-y-4">
          <div className="h-10 bg-slate-200 rounded w-1/3" />
          <div className="h-48 bg-slate-200 rounded" />
          <div className="h-32 bg-slate-200 rounded" />
        </div>
      </div>
    );
  }

  if (!job) return null;

  return (
    <div className="p-4 animate-fade-in" data-testid="job-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <button
            data-testid="back-button"
            onClick={handleBack}
            className="w-10 h-10 rounded-full bg-white border-2 border-slate-200 flex items-center justify-center"
          >
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </button>
          <span className="font-heading text-sm font-bold text-slate-500">
            #{job.id.slice(0, 8).toUpperCase()}
          </span>
        </div>
        
        <div className="flex gap-2">
          {editing ? (
            <>
              <Button
                data-testid="cancel-edit"
                variant="outline"
                size="sm"
                onClick={() => { setEditing(false); setEditForm(job); }}
              >
                Cancel
              </Button>
              <Button
                data-testid="save-job"
                size="sm"
                onClick={handleSave}
                disabled={saving}
                className="bg-[#FF5F00] hover:bg-orange-hover"
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </>
          ) : (
            <>
              <Button
                data-testid="edit-job"
                variant="outline"
                size="sm"
                onClick={handleStartEditing}
              >
                Edit
              </Button>
              <Button
                data-testid="delete-job"
                variant="outline"
                size="sm"
                onClick={handleDelete}
                className="text-red-600 border-red-200 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
      </div>
      
      {/* Main Content */}
      <div className="bg-white rounded-xl border-2 border-slate-200 shadow-hard overflow-hidden mb-4">
        <div className="p-4">
          {editing ? (
            <Input
              data-testid="edit-client-name"
              value={editForm.client_name}
              onChange={(e) => setEditForm(prev => ({ ...prev, client_name: e.target.value }))}
              className="font-heading text-2xl font-bold border-0 p-0 h-auto mb-2"
            />
          ) : job.client_id ? (
            <Link 
              to={`/client/${job.client_id}`}
              data-testid="client-name-link"
              className="font-heading text-2xl font-bold text-slate-dark mb-2 block hover:text-[#FF5F00] transition-colors underline decoration-2 underline-offset-2"
            >
              {job.client_name}
            </Link>
          ) : (
            <h1 className="font-heading text-2xl font-bold text-slate-dark mb-2">
              {job.client_name}
            </h1>
          )}
          
          {editing ? (
            <Textarea
              data-testid="edit-description"
              value={editForm.description}
              onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
              className="mb-4"
            />
          ) : (
            <p className="text-slate-600 mb-4">{job.description}</p>
          )}
          
          {/* Contact Info */}
          <div className="space-y-2 mb-4">
            {editing ? (
              <div className="grid grid-cols-2 gap-2">
                <Input
                  data-testid="edit-phone"
                  value={editForm.client_phone || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, client_phone: e.target.value }))}
                  placeholder="Phone"
                />
                <Input
                  data-testid="edit-email"
                  value={editForm.client_email || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, client_email: e.target.value }))}
                  placeholder="Email"
                />
              </div>
            ) : (
              <>
                {job.client_phone && (
                  <a 
                    href={`tel:${job.client_phone}`}
                    className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
                  >
                    <Phone className="w-4 h-4" />
                    <span>{job.client_phone}</span>
                  </a>
                )}
                {job.client_email && (
                  <a 
                    href={`mailto:${job.client_email}`}
                    className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
                  >
                    <Mail className="w-4 h-4" />
                    <span>{job.client_email}</span>
                  </a>
                )}
              </>
            )}
            
            {editing ? (
              <Input
                data-testid="edit-address"
                value={editForm.address || ''}
                onChange={(e) => setEditForm(prev => ({ ...prev, address: e.target.value }))}
                placeholder="Address"
              />
            ) : job.address && (
              <a 
                href={`https://maps.google.com/?q=${encodeURIComponent(job.address)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
              >
                <MapPin className="w-4 h-4" />
                <span>{job.address}</span>
              </a>
            )}
          </div>
          
          {/* Amount Breakdown */}
          <div className="bg-slate-50 rounded-lg p-4 mb-4">
            <h3 className="font-bold text-slate-700 mb-3 text-sm uppercase tracking-wider">Price Breakdown</h3>
            
            <div className="space-y-2 text-sm">
              {(job.labor_amount > 0 || editing) && (
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">Labor</span>
                  {editing ? (
                    <Input
                      data-testid="edit-labor"
                      type="number"
                      step="0.01"
                      value={editForm.labor_amount || ''}
                      onChange={(e) => setEditForm(prev => ({ ...prev, labor_amount: e.target.value }))}
                      className="w-24 text-right h-8"
                      placeholder="0.00"
                    />
                  ) : (
                    <span className="text-slate-900 font-medium">${(job.labor_amount || 0).toFixed(2)}</span>
                  )}
                </div>
              )}
              
              {(job.materials_amount > 0 || editing) && (
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">Materials</span>
                  {editing ? (
                    <Input
                      data-testid="edit-materials"
                      type="number"
                      step="0.01"
                      value={editForm.materials_amount || ''}
                      onChange={(e) => setEditForm(prev => ({ ...prev, materials_amount: e.target.value }))}
                      className="w-24 text-right h-8"
                      placeholder="0.00"
                    />
                  ) : (
                    <span className="text-slate-900 font-medium">${(job.materials_amount || 0).toFixed(2)}</span>
                  )}
                </div>
              )}
              
              {((job.labor_amount > 0 || job.materials_amount > 0) && !editing) && (
                <div className="flex justify-between border-t border-slate-200 pt-2">
                  <span className="text-slate-600">Subtotal</span>
                  <span className="text-slate-900 font-medium">
                    ${((job.labor_amount || 0) + (job.materials_amount || 0)).toFixed(2)}
                  </span>
                </div>
              )}
              
              {/* Tax Breakdown */}
              {job.tax_breakdown && job.tax_breakdown.length > 0 && job.tax_breakdown.map((tax, index) => (
                <div key={index} className="flex justify-between text-slate-600">
                  <span>{tax.name} ({tax.rate}%)</span>
                  <span className="text-slate-900 font-medium">${tax.amount.toFixed(2)}</span>
                </div>
              ))}
              
              {job.tax_amount > 0 && (!job.tax_breakdown || job.tax_breakdown.length === 0) && (
                <div className="flex justify-between text-slate-600">
                  <span>Tax</span>
                  <span className="text-slate-900 font-medium">${job.tax_amount.toFixed(2)}</span>
                </div>
              )}

              {/* Billable Expenses */}
              {billableExpenses > 0 && (
                <div className="flex justify-between text-amber-700 bg-amber-50 -mx-2 px-2 py-1 rounded">
                  <span>Billable Expenses</span>
                  <span className="font-medium">${billableExpenses.toFixed(2)}</span>
                </div>
              )}
            </div>
            
            {/* Total */}
            <div className="flex items-center justify-between mt-3 pt-3 border-t-2 border-slate-300">
              <span className="text-slate-700 font-bold">Total</span>
              <span className="font-heading text-2xl font-bold text-[#FF5F00]">
                ${((job.quote_amount || 0) + billableExpenses).toFixed(2)}
              </span>
            </div>
          </div>
        </div>
        
        {/* Status Slider - Only show for non-parent recurring jobs */}
        <div className="px-4 pb-4">
          {job.is_recurring ? (
            // Parent recurring job - show recurring badge instead of status
            <div className="bg-purple-50 rounded-lg p-4 border-2 border-purple-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center">
                  <Repeat className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-heading font-bold text-purple-800">Recurring Schedule</p>
                  <p className="text-sm text-purple-600">
                    {job.recurrence_pattern === 'weekly' ? 'Weekly' : 
                     job.recurrence_pattern === 'biweekly' ? 'Every 2 weeks' : 
                     job.recurrence_pattern === 'monthly' ? 'Monthly' : job.recurrence_pattern}
                    {job.recurrence_end_date && ` until ${formatDate(job.recurrence_end_date)}`}
                  </p>
                </div>
              </div>
              <p className="text-xs text-purple-500 mt-2">
                Individual visits are invoiced separately. View scheduled visits below.
              </p>
            </div>
          ) : (
            // Regular job or child recurring job - show status slider
            <>
              <Label className="text-slate-700 uppercase text-xs tracking-wider font-bold mb-2 block">
                Job Status
              </Label>
              <StatusSlider status={job.status} onChange={handleStatusChange} />
            </>
          )}
        </div>
      </div>

      {/* Child Jobs Section - Only for parent recurring jobs */}
      {job.is_recurring && (
        <div className="bg-white rounded-xl border-2 border-slate-200 shadow-hard p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading text-lg font-bold text-slate-dark flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Scheduled Visits
            </h2>
            <span className="text-sm text-slate-500">
              {childJobs.filter(c => c.status !== 'paid').length} unpaid
            </span>
          </div>
          
          {loadingChildren ? (
            <div className="animate-pulse space-y-2">
              <div className="h-16 bg-slate-100 rounded" />
              <div className="h-16 bg-slate-100 rounded" />
            </div>
          ) : childJobs.length > 0 ? (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {childJobs.map((child) => (
                <Link
                  key={child.id}
                  to={`/jobs/${child.id}`}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${
                      child.status === 'paid' ? 'bg-green-500' :
                      child.status === 'invoiced' ? 'bg-purple-500' :
                      child.status === 'scheduled' ? 'bg-amber-500' : 'bg-blue-500'
                    }`} />
                    <div>
                      <p className="font-medium text-slate-800 text-sm">
                        {formatDate(child.scheduled_date)}
                      </p>
                      <p className="text-xs text-slate-500 capitalize">{child.status}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-slate-800">${(child.quote_amount || 0).toFixed(2)}</p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-slate-400 text-sm text-center py-4">No scheduled visits yet</p>
          )}
        </div>
      )}

      {/* Schedule Section */}
      <div className="bg-white rounded-xl border-2 border-slate-200 shadow-hard p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-lg font-bold text-slate-dark flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Schedule
          </h2>
          <Button
            data-testid="schedule-job-btn"
            size="sm"
            onClick={() => setShowScheduleModal(true)}
            className="bg-slate-dark hover:bg-slate-darker"
          >
            {job.scheduled_date ? 'Reschedule' : 'Schedule'}
          </Button>
        </div>
        
        {job.scheduled_date ? (
          <div className="space-y-3">
            {/* Next Appointment */}
            <div className="bg-slate-50 rounded-lg p-3">
              <div className="text-xs text-slate-500 font-bold uppercase tracking-wider mb-2">
                {job.is_exception ? 'Modified Appointment' : 'Next Appointment'}
              </div>
              <div className="flex items-center gap-3 mb-1">
                <Calendar className="w-4 h-4 text-[#FF5F00]" />
                <span className="font-medium text-slate-900">{formatDate(job.scheduled_date)}</span>
                {job.is_exception && (
                  <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">
                    Rescheduled
                  </span>
                )}
              </div>
              {job.scheduled_time && (
                <div className="flex items-center gap-3">
                  <Clock className="w-4 h-4 text-[#FF5F00]" />
                  <span className="text-slate-700">
                    {formatTime(job.scheduled_time)}
                    {job.duration && ` (${job.duration} min)`}
                  </span>
                </div>
              )}
              {/* Assigned User */}
              {job.assigned_to_name && (
                <div className="flex items-center gap-3 mt-2 pt-2 border-t border-slate-200">
                  <User className="w-4 h-4 text-blue-600" />
                  <span className="text-slate-700">Assigned to: <strong className="text-blue-600">{job.assigned_to_name}</strong></span>
                </div>
              )}
            </div>

            {/* Recurring Pattern Info */}
            {(job.is_recurring || job.parent_job_id) && (
              <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                <div className="flex items-center gap-2 text-purple-700">
                  <Repeat className="w-4 h-4" />
                  <span className="font-medium">
                    {job.is_recurring ? (
                      <>
                        Repeats {job.recurrence_pattern === 'weekly' ? 'weekly' : 
                                 job.recurrence_pattern === 'biweekly' ? 'every 2 weeks' : 
                                 job.recurrence_pattern === 'monthly' ? 'monthly' : job.recurrence_pattern}
                        {job.recurrence_end_date && (
                          <span className="text-purple-500 text-sm"> until {formatDate(job.recurrence_end_date)}</span>
                        )}
                      </>
                    ) : (
                      'Part of recurring series'
                    )}
                  </span>
                </div>
                {job.is_exception && job.original_scheduled_date && (
                  <div className="text-purple-600 text-sm mt-1">
                    Originally scheduled for {formatDate(job.original_scheduled_date)}
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <p className="text-slate-400 text-sm">No schedule set. Tap Schedule to add date & time.</p>
        )}
      </div>

      {/* Access Details Section */}
      {(job.gate_code || job.lockbox_code || job.access_notes) && (
        <div className="bg-amber-50 rounded-xl border-2 border-amber-200 p-4 mb-4">
          <div className="flex items-start gap-3">
            <Key className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h2 className="font-heading text-lg font-bold text-amber-800 mb-2">Access Details</h2>
              <div className="space-y-1 text-sm text-amber-800">
                {job.gate_code && <p><strong>Gate Code:</strong> {job.gate_code}</p>}
                {job.lockbox_code && <p><strong>Lockbox Code:</strong> {job.lockbox_code}</p>}
                {job.access_notes && <p><strong>Notes:</strong> {job.access_notes}</p>}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Photos Section */}
      <div className="bg-white rounded-xl border-2 border-slate-200 shadow-hard p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-lg font-bold text-slate-dark">
            Job Photos
          </h2>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handlePhotoUpload}
            className="hidden"
          />
          <Button
            data-testid="add-photo"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadingPhoto}
            className="bg-slate-dark hover:bg-slate-darker"
          >
            <Camera className="w-4 h-4 mr-1" />
            {uploadingPhoto ? 'Uploading...' : 'Add Photo'}
          </Button>
        </div>
        
        {job.photos?.length > 0 ? (
          <div className="grid grid-cols-2 gap-2">
            {job.photos.map((photo) => (
              <div key={photo.id} className="relative group">
                <img 
                  src={photo.data} 
                  alt={photo.description || 'Job photo'}
                  className="w-full h-32 object-cover rounded-lg"
                />
                <button
                  data-testid={`delete-photo-${photo.id}`}
                  onClick={() => handleDeletePhoto(photo.id)}
                  className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-4 h-4" />
                </button>
                {photo.description && (
                  <p className="text-xs text-slate-500 mt-1 truncate">{photo.description}</p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-slate-400">
            <Image className="w-8 h-8 mx-auto mb-2" />
            <p className="text-sm">No photos yet</p>
          </div>
        )}
      </div>
      
      {/* Job Expenses Section */}
      <div className="mb-4">
        <JobExpenses 
          jobId={id} 
          quoteAmount={job.quote_amount || job.labor_amount + (job.materials_amount || 0)}
          onExpenseChange={setBillableExpenses}
        />
      </div>

      {/* Customer Notes Section - Visible to customers */}
      <div className="bg-white rounded-xl border-2 border-slate-200 shadow-hard p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="font-heading text-lg font-bold text-slate-dark">
              Service Notes
            </h2>
            <p className="text-xs text-slate-500">Visible to customer</p>
          </div>
          {editing && activeTemplate && (!job.customer_notes || job.customer_notes.trim() === '') && (
            <span className="text-xs text-emerald-600 bg-emerald-50 px-2 py-1 rounded">
              Template: {activeTemplate.template_name}
            </span>
          )}
        </div>
        {editing ? (
          <Textarea
            data-testid="edit-customer-notes"
            value={editForm.customer_notes || ''}
            onChange={(e) => setEditForm(prev => ({ ...prev, customer_notes: e.target.value }))}
            placeholder="Add service notes for the customer (checklist, work completed, etc.)..."
            className="min-h-[120px]"
          />
        ) : (
          <p className="text-slate-600 whitespace-pre-wrap">
            {job.customer_notes || 'No service notes yet.'}
          </p>
        )}
      </div>

      {/* Private Notes Section - Internal only */}
      <div className="bg-slate-50 rounded-xl border-2 border-slate-200 shadow-hard p-4 mb-4">
        <div className="mb-3">
          <h2 className="font-heading text-lg font-bold text-slate-dark">
            Private Notes
          </h2>
          <p className="text-xs text-slate-500">Internal only - not visible to customer</p>
        </div>
        {editing ? (
          <Textarea
            data-testid="edit-notes"
            value={editForm.notes || ''}
            onChange={(e) => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
            placeholder="Add private internal notes..."
            className="min-h-[80px] bg-white"
          />
        ) : (
          <p className="text-slate-600 whitespace-pre-wrap">
            {job.notes || 'No private notes.'}
          </p>
        )}
      </div>
      
      {/* Action Buttons - Bottom Fixed */}
      <div className="space-y-3 pb-4">
        {/* PDF and Portal Link Row */}
        <div className="flex gap-2">
          <Button
            data-testid="download-pdf-btn"
            onClick={handleDownloadPdf}
            disabled={downloadingPdf}
            variant="outline"
            className="flex-1 h-12 font-bold border-2"
          >
            {downloadingPdf ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileDown className="w-4 h-4 mr-2" />
            )}
            PDF
          </Button>
          <Button
            data-testid="copy-link-btn"
            onClick={handleCopyPortalLink}
            variant="outline"
            className="flex-1 h-12 font-bold border-2"
          >
            <LinkIcon className="w-4 h-4 mr-2" />
            Copy Link
          </Button>
        </div>

        {/* SMS/Email Row */}
        <div className="flex gap-2">
          <Button
            data-testid="send-sms-btn"
            onClick={() => setShowSMSModal(true)}
            variant="outline"
            className="flex-1 h-12 font-bold border-2"
          >
            <MessageSquare className="w-4 h-4 mr-2" />
            SMS
          </Button>
          <Button
            data-testid="send-email-btn"
            onClick={() => setShowEmailModal(true)}
            variant="outline"
            className="flex-1 h-12 font-bold border-2"
          >
            <Mail className="w-4 h-4 mr-2" />
            Email
          </Button>
        </div>
        
        <Button
          data-testid="request-payment"
          onClick={() => {
            // Check if plan has in-app messaging
            const hasInAppSms = planLimits?.has_native_sms;
            const hasInAppEmail = planLimits?.has_native_email;
            
            if (hasInAppSms || hasInAppEmail) {
              // Show payment method choice modal
              setShowPaymentModal(true);
            } else {
              // Use native SMS
              handleRequestPayment();
            }
          }}
          className="w-full h-14 bg-[#FF5F00] hover:bg-orange-hover text-white text-lg font-bold"
        >
          <DollarSign className="w-5 h-5 mr-2" />
          Request Payment
        </Button>
      </div>

      {/* Payment Request Modal - choose SMS or Email */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full">
            <h3 className="text-lg font-bold text-slate-800 mb-2">Send Payment Request</h3>
            <p className="text-slate-600 text-sm mb-4">How would you like to send the invoice?</p>
            
            <div className="space-y-3">
              {planLimits?.has_native_email && (
                <button
                  onClick={() => {
                    setShowPaymentModal(false);
                    setShowEmailModal(true);
                  }}
                  className="w-full flex items-center gap-3 p-4 bg-blue-50 hover:bg-blue-100 rounded-lg border border-blue-200 transition-colors"
                >
                  <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                    <Mail className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-left">
                    <p className="font-bold text-slate-800">Email</p>
                    <p className="text-sm text-slate-600">Send invoice via email</p>
                  </div>
                </button>
              )}
              
              {planLimits?.has_native_sms && (
                <button
                  onClick={() => {
                    setShowPaymentModal(false);
                    setShowSMSModal(true);
                  }}
                  className="w-full flex items-center gap-3 p-4 bg-emerald-50 hover:bg-emerald-100 rounded-lg border border-emerald-200 transition-colors"
                >
                  <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-left">
                    <p className="font-bold text-slate-800">SMS</p>
                    <p className="text-sm text-slate-600">Send invoice via text message</p>
                  </div>
                </button>
              )}
              
              <button
                onClick={() => {
                  setShowPaymentModal(false);
                  handleRequestPayment();
                }}
                className="w-full flex items-center gap-3 p-4 bg-slate-50 hover:bg-slate-100 rounded-lg border border-slate-200 transition-colors"
              >
                <div className="w-10 h-10 bg-slate-500 rounded-full flex items-center justify-center">
                  <Phone className="w-5 h-5 text-white" />
                </div>
                <div className="text-left">
                  <p className="font-bold text-slate-800">Native SMS</p>
                  <p className="text-sm text-slate-600">Open phone's messaging app</p>
                </div>
              </button>
            </div>
            
            <button
              onClick={() => setShowPaymentModal(false)}
              className="w-full mt-4 py-2 text-slate-600 hover:text-slate-800 text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Modals */}
      <ScheduleModal
        job={job}
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        onScheduled={handleScheduled}
      />
      
      <SMSOptionsModal
        job={job}
        isOpen={showSMSModal}
        onClose={() => setShowSMSModal(false)}
        mode="sms"
        onSuccess={fetchJob}
      />

      <SMSOptionsModal
        job={job}
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        mode="email"
        onSuccess={fetchJob}
      />
    </div>
  );
}
